import { useContext, useEffect, useMemo, useState } from 'react'
import { UIContext } from 'Context/UI'
import { TableContext } from 'Context/Table'
import { useParams } from 'react-router-dom'

import ProductCell from 'Presentation/Components/ContractCard/Cells/ProductCell'
import TitleCell from 'Presentation/Components/KumopackRequestCells/TitleCell'
import QuantityCell from 'Presentation/Components/KumopackRequestCells/QuantityCell'
import MultiplePriceCell from 'Presentation/Components/RequestCard/Components/MultiplePrice'

import { useLang } from '_metronic/i18n/Metronici18n'
import { useContractStore } from 'Store/Contract'
import Swal from 'sweetalert2'
import { shallow } from 'zustand/shallow'
import formatNumberCommas from 'Utils/formatNumberCommas'
import { toast } from 'react-toastify'
import * as Yup from 'yup'
import formatThaiPhoneNumber from 'Utils/formatThaiPhoneNumber'

const ViewModel = () => {
	const lang = useLang()
	const validationSchema = Yup.object().shape({
		// tracking: Yup.string().required('กรุณากรอกหมายเลขที่พัสดุ'),
		evidencePicture: Yup.mixed().required('กรุณาแนบรูปภาพหลักฐานการส่งสินค้า'),
	})
	const { uuId } = useParams<{ uuId: string }>()
	const { updateLoading, updateError, updateSorting, updatePagination } = useContext(TableContext)
	const { setShowContentToolbar, setCustomContentToolbar } = useContext(UIContext)
	const { data, getOne, clearState } = useContractStore(
		state => ({
			data: state.selected,
			getOne: state.getOne,
			clearState: state.clearState,
		}),
		shallow
	)
	const items = useMemo(
		() =>
			(data.items ?? [])
				.reduce((acc: any[], item: any) => {
					const added = acc.find(
						(aItem: any) =>
							aItem.product?.id === item.productId &&
							aItem.oemRequestItemId === item.oemRequestItemId
					)

					if (added) {
						const index = acc.findIndex(
							(aItem: any) =>
								aItem.product?.id === item.productId &&
								aItem.oemRequestItemId === item.oemRequestItemId
						)
						acc[index].quantities.push({ quantity: item.quantity ?? 0 })
						acc[index].prices.push({ price: item.price ? parseFloat(item.price) : 0 })
						acc[index].total.push({ price: item.total ? parseFloat(item.total) : 0 })
					} else {
						acc.push({
							id: item.id,
							oemRequestItemId: item.oemRequestItemId,
							documentStatus: data.documentStatus,
							product: item?.oemRequestItem?.product ?? {},
							dimension: item?.oemRequestItem?.dimension ?? {},
							options: item?.oemRequestItem?.options ?? [],
							accessories: item?.oemRequestItem?.accessories ?? [],
							thickness: item?.oemRequestItem?.thickness ?? {},
							outerMaterial: item?.oemRequestItem?.outerMaterial ?? {},
							innerMaterial: item?.oemRequestItem?.innerMaterial ?? {},
							printingOption: item?.oemRequestItem?.printingOption ?? {},
							forming: item?.oemRequestItem?.forming ?? {},
							sustainabilities: item?.oemRequestItem?.sustainabilities ?? [],
							certificates: item?.oemRequestItem?.certificates ?? [],
							quantities: [{ quantity: item.quantity ?? 0 }],
							prices: [{ price: item.price ? parseFloat(item.price) : 0 }],
							total: [{ price: item.total ? parseFloat(item.total) : 0 }],
							title: item.title,
							isFixed: item.isFixed,
							remark: item.remark,
							isDelete: false,
							isCreate: false,
						})
					}

					return acc
				}, [])
				.filter((item: any) => item.isFixed),
		[data]
	)
	const otherItems = useMemo(
		() =>
			(data.items ?? [])
				.reduce((acc: any[], item: any) => {
					const added = acc.find(
						(aItem: any) =>
							aItem.product?.id === item.productId &&
							aItem.oemRequestItemId === item.oemRequestItemId
					)

					if (added) {
						const index = acc.findIndex(
							(aItem: any) =>
								aItem.product?.id === item.productId &&
								aItem.oemRequestItemId === item.oemRequestItemId
						)
						acc[index].quantities.push({ quantity: item.quantity ?? 0 })
						acc[index].prices.push({ price: item.price ? parseFloat(item.price) : 0 })
						acc[index].total.push({ price: item.total ? parseFloat(item.total) : 0 })
					} else {
						acc.push({
							id: item.id,
							oemRequestItemId: item.oemRequestItemId,
							documentStatus: data.documentStatus,
							product: item?.oemRequestItem?.product ?? {},
							dimension: item?.oemRequestItem?.dimension ?? {},
							options: item?.oemRequestItem?.options ?? [],
							accessories: item?.oemRequestItem?.accessories ?? [],
							thickness: item?.oemRequestItem?.thickness ?? {},
							outerMaterial: item?.oemRequestItem?.outerMaterial ?? {},
							innerMaterial: item?.oemRequestItem?.innerMaterial ?? {},
							printingOption: item?.oemRequestItem?.printingOption ?? {},
							forming: item?.oemRequestItem?.forming ?? {},
							sustainabilities: item?.oemRequestItem?.sustainabilities ?? [],
							certificates: item?.oemRequestItem?.certificates ?? [],
							quantities: [{ quantity: item.quantity ?? 0 }],
							prices: [{ price: item.price ? parseFloat(item.price) : 0 }],
							total: [{ price: item.total ? parseFloat(item.total) : 0 }],
							title: item.title,
							isFixed: item.isFixed,
							isDelete: false,
							isCreate: false,
						})
					}

					return acc
				}, [])
				.filter((item: any) => !item.isFixed),
		[data]
	)

	const billingStatus: { status: 'done' | 'active' | 'pending'; date: string | null } =
		useMemo(() => {
			let status: 'done' | 'active' | 'pending' = 'pending'

			if (data?.status === '10') {
				status = 'pending'
			} else if (data?.status === '15') {
				status = 'active'
			} else {
				status = 'done'
			}

			return {
				status,
				date:
					data?.paymentTransactions?.[0]?.createdAt && data?.paymentTransactions[0]?.status === '20'
						? data?.paymentTransactions?.[0]?.createdAt
						: null,
			}
		}, [data])
	const approveSampleStatus: { status: 'done' | 'active' | 'pending'; date: string | null } =
		useMemo(() => {
			let status: 'done' | 'active' | 'pending' = 'pending'

			if (data?.status !== '10' && data?.status !== '15' && data?.status !== '20') {
				if (data?.status === '30') {
					status = 'active'
				} else {
					status = 'done'
				}
			} else {
				status = 'pending'
			}

			return {
				status,
				date: data?.billings?.[0]?.paidAt ?? null,
			}
		}, [data])
	const producingStatus: 'done' | 'active' | 'pending' = useMemo(() => {
		if (['10', '15', '20', '30', '40'].includes(data?.status)) {
			return 'pending'
		} else if (data?.status === '50') {
			return 'active'
		} else {
			return 'done'
		}
	}, [data])
	const deliverStatus: { status: 'done' | 'active' | 'pending'; date: string | null } =
		useMemo(() => {
			let status: 'done' | 'active' | 'pending' = 'pending'

			if (data?.status === '60') {
				status = 'active'
			} else if (['10', '15', '20', '30', '40', '50'].includes(data?.status)) {
				status = 'pending'
			} else {
				status = 'done'
			}

			return {
				status,
				date: data?.sentProductAt ?? null,
			}
		}, [data])
	const cancelStatus: { status: 'done' | 'active' | 'pending'; date: string | null } =
		useMemo(() => {
			let status: 'done' | 'active' | 'pending' = 'pending'

			if (data?.status === '90') {
				status = 'done'
			} else {
				status = 'pending'
			}

			return {
				status,
				date: data?.cancelledAt ?? null,
			}
		}, [data])

	const deliveryAddress = useMemo(() => {
		const rawData = data?.oemRequest?.deliveryAddress
		if (rawData !== undefined && rawData !== null) {
			let text = ''
			if (rawData?.receiverFullname) {
				text += rawData?.receiverFullname
			}
			if (rawData?.receiverPhone) {
				text += ` ${formatThaiPhoneNumber(rawData?.receiverPhone)} <br/>`
			}
			if (rawData?.address) {
				text += ` ${rawData?.address}`
			}
			if (lang === 'en') {
				text += ` ${rawData?.province?.nameEn} ${rawData?.amphure?.nameEn} ${rawData?.tumbon?.nameEn}`
			} else {
				text += ` ${rawData?.province?.nameTh} ${rawData?.amphure?.nameTh} ${rawData?.tumbon?.nameTh}`
			}
			if (rawData?.tumbon?.zipCode) {
				text += ` ${rawData?.tumbon?.zipCode}`
			}

			return text
		} else {
			return ''
		}
	}, [data, lang])

	const deliverSample = useContractStore(state => state.deliverSample)
	const deliverProduct = useContractStore(state => state.deliverProduct)

	const [deliverFormState, setDeliverFormState] = useState({
		tracking: '',
		evidencePicture: null,
	})

	const [isLoading, setIsLoading] = useState<boolean>(false)
	const [isSubmitting, setIsSubmitting] = useState<boolean>(false)
	const [error, setError] = useState<any>({})

	const canDeliverSample = data?.status === '20'
	const canDeliverProduct = data?.status === '50'

	const onChangeDeliverFormState = (key: string, value: any) => {
		setDeliverFormState({
			...deliverFormState,
			[key]: value,
		})
	}

	const fetchData = () => {
		if (uuId) {
			setIsLoading(true)
			updateLoading(true)
			updateError(false)

			getOne(uuId).then(({ success, data }) => {
				if (!success) {
					Swal.fire({
						icon: 'error',
						title: 'เกิดข้อผิดพลาด',
						text: data ?? 'ไม่สามารถดึงข้อมูลได้',
						showConfirmButton: false,
						timer: 1500,
						customClass: {
							confirmButton: 'btn btn-sm btn-kumopack-primary-600 text-kumopack-base-white',
						},
					}).then(() => {
						updateError(true)
					})
				} else {
					// // Set Properties
					// const oemRequest = data.quotation.inquiry.oemRequest
					// setProperties({
					// 	productLineParent: oemRequest.product.productLine?.parent?.nameTh,
					// 	productLine: oemRequest.product.productLine.nameTh,
					// 	options: oemRequest.options,
					// 	dimension: oemRequest.dimension,
					// 	thickness: oemRequest.thickness,
					// 	outerMaterial: oemRequest.outerMaterial,
					// 	innerMaterial: oemRequest.innerMaterial,
					// 	printingOption: oemRequest.printingOption,
					// 	sustainabilities: oemRequest.sustainabilities,
					// 	certificates: oemRequest.certificates,
					// })

					setIsLoading(false)
					updateLoading(false)
					updateError(false)
				}
			})
		}
	}

	const onSendSample = () => {
		deliverSample(uuId ?? '', {
			sentSampleTracking: deliverFormState.tracking,
			sentSampleEvidencePicture: deliverFormState.evidencePicture,
		}).then(({ success, data }) => {
			if (!success) {
				Swal.fire({
					icon: 'error',
					title: 'เกิดข้อผิดพลาด',
					text: data ?? 'ไม่สามารถส่งข้อมูลสินค้าตัวอย่างได้',
					showConfirmButton: false,
					timer: 1500,
					customClass: {
						confirmButton: 'btn btn-sm btn-kumopack-primary-600 text-kumopack-base-white',
					},
				}).then(() => {
					setIsSubmitting(false)
				})
			} else {
				Swal.fire({
					icon: 'success',
					title: 'ส่งข้อมูลสินค้าตัวอย่างสำเร็จ',
					showConfirmButton: false,
					timer: 1500,
					customClass: {
						confirmButton: 'btn btn-sm btn-kumopack-primary-600 text-kumopack-base-white',
					},
				}).then(() => {
					setIsSubmitting(false)
					fetchData()
				})
			}
		})
	}

	const onSendProduct = () => {
		deliverProduct(uuId ?? '', {
			sentProductsTracking: deliverFormState.tracking,
			sentProductsEvidencePicture: deliverFormState.evidencePicture,
		}).then(({ success, data }) => {
			if (!success) {
				Swal.fire({
					icon: 'error',
					title: 'เกิดข้อผิดพลาด',
					text: data ?? 'ไม่สามารถส่งข้อมูลสินค้าได้',
					showConfirmButton: false,
					timer: 1500,
					customClass: {
						confirmButton: 'btn btn-sm btn-kumopack-primary-600 text-kumopack-base-white',
					},
				}).then(() => {
					setIsSubmitting(false)
				})
			} else {
				Swal.fire({
					icon: 'success',
					title: 'ส่งข้อมูลสินค้าสำเร็จ',
					showConfirmButton: false,
					timer: 1500,
					customClass: {
						confirmButton: 'btn btn-sm btn-kumopack-primary-600 text-kumopack-base-white',
					},
				}).then(() => {
					setIsSubmitting(false)
					fetchData()
				})
			}
		})
	}

	const onSubmitDeliveryForm = () => {
		setIsSubmitting(true)

		validationSchema
			.validate(deliverFormState, { abortEarly: false })
			.then(() => {
				setError({})

				if (canDeliverSample) {
					onSendSample()
				} else if (canDeliverProduct) {
					onSendProduct()
				}
			})
			.catch(err => {
				toast.error('กรุณากรอกข้อมูลให้ครบถ้วน')
				setError(
					err.inner.reduce((all: any[], e: any) => {
						all[e.path] = e.message

						return all
					}, {})
				)
				setIsSubmitting(false)
			})
	}

	const PRODUCTION_TABLE_CONFIGS: any[] = [
		{
			Header: 'ชื่อ',
			accessor: 'product',
			width: 400,
			minWidth: 400,
			Cell: ({ row }: any) => (
				<ProductCell
					{...row.original.product}
					dimension={row.original?.dimension}
					options={row.original?.options}
					accessories={row.original?.accessories}
					thickness={row.original?.thickness}
					outerMaterial={row.original?.outerMaterial}
					innerMaterial={row.original?.innerMaterial}
					printingOption={row.original?.printingOption}
					forming={row.original?.forming}
					sustainabilities={row.original?.sustainabilities}
					certificates={row.original?.certificates}
					note={row.original?.remark}
				/>
			),
		},
		{
			Header: () => <div className="text-end">จำนวนที่สั่งผลิต</div>,
			headerRight: true,
			accessor: 'quantities',
			width: 150,
			minWidth: 150,
			Cell: ({ value }: any) => (
				<div className="text-end">
					{value.map((item: any, index: number) => (
						<div key={index}>
							<div className="d-inline-block me-2 fs-4">x</div>
							<div className="d-inline-block text-kumopack-grey-900 fw-bolder fs-4 me-2">
								{formatNumberCommas(item.quantity ?? 0)}
							</div>
							<div className="d-inline-block text-kumopack-grey-600">
								{lang === 'en' ? 'pcs' : 'ชิ้น'}
							</div>
						</div>
					))}
				</div>
			),
		},
		{
			Header: () => <div className="text-end">ราคาต่อชิ้น</div>,
			headerRight: true,
			accessor: 'prices',
			width: 120,
			minWidth: 120,
			Cell: ({ value }: any) => {
				return <MultiplePriceCell prices={value} />
			},
		},
		{
			Header: () => <div className="text-end">ยอดรวม</div>,
			headerRight: true,
			accessor: 'total',
			width: 120,
			minWidth: 120,
			Cell: ({ value }: any) => {
				return <MultiplePriceCell prices={value} />
			},
		},
	]

	const OTHER_TABLE_CONFIGS: any[] = [
		{
			Header: 'ชื่อ',
			accessor: 'product',
			width: 600,
			minWidth: 600,
			Cell: ({ row }: any) => (
				<TitleCell
					isFixed={row.original.isFixed}
					title={row.original?.title}
					nameTh={row.original?.product?.nameTh}
					nameEn={row.original?.product?.nameEn}
					featurePicturePath={row.original?.product?.featurePicturePath}
					shortDescription={row.original?.product?.shortDescription}
				/>
			),
		},
		{
			Header: () => <div className="text-end">จำนวน</div>,
			headerRight: true,
			accessor: 'quantities',
			width: 150,
			minWidth: 150,
			Cell: ({ value }: any) => {
				return (
					<QuantityCell
						isFixed={true}
						quantity={value?.[0]?.quantity ?? 0}
						unit={lang === 'en' ? 'items' : 'รายการ'}
					/>
				)
			},
		},
		{
			Header: () => <div className="text-end">ราคาต่อรายการ</div>,
			headerRight: true,
			accessor: 'prices',
			width: 120,
			minWidth: 120,
			Cell: ({ value }: any) => {
				return <MultiplePriceCell prices={value} />
			},
		},
		{
			Header: () => <div className="text-end">ยอดรวม</div>,
			headerRight: true,
			accessor: 'total',
			width: 120,
			minWidth: 120,
			Cell: ({ value }: any) => {
				return <MultiplePriceCell prices={value} />
			},
		},
	]

	useEffect(() => {
		fetchData()
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [])

	useEffect(() => {
		setShowContentToolbar(false)
		setCustomContentToolbar(null)
		updateSorting('product', false)
		updatePagination(false)

		return () => {
			setShowContentToolbar(true)
			setCustomContentToolbar(null)
			clearState()
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [])

	return {
		billingStatus,
		approveSampleStatus,
		producingStatus,
		deliverStatus,
		cancelStatus,
		canDeliverSample,
		canDeliverProduct,
		isLoading,
		isSubmitting,
		items,
		otherItems,
		data,
		error,
		deliveryAddress,
		deliverFormState,
		onChangeDeliverFormState,
		onSubmitDeliveryForm,
		PRODUCTION_TABLE_CONFIGS,
		OTHER_TABLE_CONFIGS,
	}
}

export default ViewModel
